import React from "react";
import {
  Clock,
  Shield,
  Target,
  TrendingUp,
  ChartBar,
  Award,
} from "lucide-react";

const PrimingScreen = ({ onStart }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header Section with Breathing Room */}
        <div className="text-center mb-10">
          <div className="inline-block bg-slate-800 text-white px-6 py-2 rounded text-sm font-medium mb-8 tracking-wide">
            FREE 6-Minute Revenue Leak Assessment
          </div>

          <h1
            className="font-serif text-5xl md:text-6xl font-normal text-slate-900 mb-10 leading-tight"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Uncover Hidden Revenue Leaks
            <span className="block text-slate-600 mt-2">
              in Your Business Operations
            </span>
          </h1>

          <div className="max-w-3xl mx-auto">
            <p
              className="text-xl text-slate-600 leading-relaxed mb-2"
              style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
            >
              Struggling to grow your business while working longer hours? This
              6-minute assessment pinpoints exactly where your leads are
              slipping away, which tasks are stealing your precious time, and
              what broken systems are draining your revenue—giving you a clear
              picture of your business's critical gaps.
            </p>
          </div>
        </div>

        {/* Professional Divider */}
        <div className="w-24 h-px bg-slate-300 mx-auto mb-6"></div>

        {/* What You'll Discover Section */}
        <div className="mb-10">
          <h2
            className="font-serif text-3xl text-slate-900 text-center mb-16"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            What Your Assessment Will Reveal
          </h2>

          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 bg-slate-100 rounded-lg p-4">
                  <TrendingUp className="w-7 h-7 text-slate-700" />
                </div>
                <div>
                  <h3
                    className="text-xl font-semibold text-slate-900 mb-3"
                    style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
                  >
                    Revenue Recovery Analysis
                  </h3>
                  <p
                    className="text-slate-600 text-lg leading-relaxed"
                    style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
                  >
                    Identify exactly where leads are slipping through: missed
                    calls, slow responses, broken forms—and how to plug the
                    holes.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 bg-slate-100 rounded-lg p-4">
                  <Clock className="w-7 h-7 text-slate-700" />
                </div>
                <div>
                  <h3
                    className="text-xl font-semibold text-slate-900 mb-3"
                    style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
                  >
                    Time Allocation Assessment
                  </h3>
                  <p
                    className="text-slate-600 text-lg leading-relaxed"
                    style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
                  >
                    Detailed analysis of where your highest-value hours are
                    being consumed by administrative tasks that could be
                    systematized or eliminated.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 bg-slate-100 rounded-lg p-4">
                  <Target className="w-7 h-7 text-slate-700" />
                </div>
                <div>
                  <h3
                    className="text-xl font-semibold text-slate-900 mb-3"
                    style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
                  >
                    Process Improvement Ideas
                  </h3>
                  <p
                    className="text-slate-600 text-lg leading-relaxed"
                    style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
                  >
                    Get practical suggestions for streamlining your daily
                    operations and discover which tasks could benefit most from
                    automation, based on your unique business workflow.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Divider */}
        <div className="w-24 h-px bg-slate-300 mx-auto mb-8"></div>

        {/* Industry Insight Box */}
        <div className="bg-amber-50 border-l-4 border-amber-400 p-10 mb-12 rounded-r-lg">
          <div className="flex items-start space-x-4">
            <Award className="w-8 h-8 text-amber-600 mt-1 flex-shrink-0" />
            <div>
              <h3
                className="text-xl font-semibold text-slate-900 mb-4"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                Industry Benchmark Insight
              </h3>
              <p
                className="text-slate-700 text-lg leading-relaxed"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                Research from Harvard Business Review and McKinsey shows that
                service businesses can lose 20-35% of potential revenue through
                inefficient lead management and manual processes. This
                assessment helps identify where these revenue leaks may be
                occurring in your operation.
              </p>
            </div>
          </div>
        </div>

        {/* Assessment Details */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-10 mb-16">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="bg-slate-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-slate-600" />
              </div>
              <h4
                className="font-semibold text-slate-900 mb-2"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                6-Minute Assessment
              </h4>
              <p
                className="text-slate-600 text-sm"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                Strategic questions only
              </p>
            </div>

            <div>
              <div className="bg-slate-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-slate-600" />
              </div>
              <h4
                className="font-semibold text-slate-900 mb-2"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                Strictly Confidential
              </h4>
              <p
                className="text-slate-600 text-sm"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                Your data remains secure
              </p>
            </div>

            <div>
              <div className="bg-slate-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-slate-600" />
              </div>
              <h4
                className="font-semibold text-slate-900 mb-2"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                Expert Analysis
              </h4>
              <p
                className="text-slate-600 text-sm"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                Business operations focus
              </p>
            </div>

            <div>
              <div className="bg-slate-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <ChartBar className="w-8 h-8 text-slate-600" />
              </div>
              <h4
                className="font-semibold text-slate-900 mb-2"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                Detailed Report
              </h4>
              <p
                className="text-slate-600 text-sm"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
              >
                Delivered within 2 - 4 hours
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <button
            onClick={onStart}
            className="bg-slate-800 hover:bg-slate-900 text-white font-semibold py-4 px-12 rounded-lg transition-all duration-200 text-lg shadow-lg hover:shadow-xl"
            style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
          >
            Begin Free Leak Assessment
          </button>

          <p
            className="text-slate-500 mt-6 text-lg"
            style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
          >
            Actionable insights • Comprehensive analysis • Professional
            consultation available
          </p>

          <div className="mt-8 pt-8 border-t border-slate-200">
            <p
              className="text-slate-600 italic"
              style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
            >
              — Your Business Efficiency Specialists
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrimingScreen;
